import { useRef, useState } from 'react';
import { useNextStep } from '../../app/navigation';
import { Screen } from '../../chrome';
import { Button, Hint, Icon, Question, RouteStrip, Sub, TextField } from '../../components';
import { useT } from '../../i18n';
import { useTrack } from '../../lib/analytics';
import { useTrip } from '../../lib/funnel';
import { useStage } from '../S1/trip';
import { Calendar } from './Calendar';
import { fromISO, toISO, today } from './dates';
import './S2.css';

/**
 * How far back our flight data reaches (architecture.md §4.4). Older dates are accepted;
 * S3 then gets `beyond_horizon` from the API and goes to manual entry. The prototype's
 * figure; the API's own horizon decides what S3 shows.
 */
const HORIZON_DAYS = 365;

/** '2026-09-08' → '08/09/2026' */
const toTyped = (iso: string | null) => (iso ? iso.split('-').reverse().join('/') : '');

/** The prototype's dd/mm/yyyy mask: digits only, slashes added as you type. */
function mask(raw: string) {
  const v = raw.replace(/\D/g, '').slice(0, 8);
  if (v.length > 4) return `${v.slice(0, 2)}/${v.slice(2, 4)}/${v.slice(4)}`;
  if (v.length > 2) return `${v.slice(0, 2)}/${v.slice(2)}`;
  return v;
}

/** A complete, real, not-future dd/mm/yyyy → ISO; anything else → null. */
function parseTyped(v: string): string | null {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(v);
  if (!m) return null;
  const d = new Date(+m[3]!, +m[2]! - 1, +m[1]!);
  if (d.getDate() !== +m[1]! || d.getMonth() !== +m[2]! - 1 || d > today()) return null;
  return toISO(d);
}

const SHORTCUTS = [
  { days: 1, key: 'S2.yesterday' },
  { days: 0, key: 'S2.today' },
  { days: 2, key: 'S2.twoDaysAgo' },
] as const;

/** S2 · When was the flight? Calendar open on arrival, shortcuts, typing allowed. */
export default function ScreenS2() {
  const t = useT();
  const track = useTrack();
  const { trip } = useStage('S2');
  const { save, isSaving } = useTrip();
  const { goNext } = useNextStep();
  const calRef = useRef<HTMLDivElement>(null);

  const [date, setDate] = useState<string | null>(trip.date);
  const [typed, setTyped] = useState(toTyped(trip.date));
  const [calOpen, setCalOpen] = useState(true);
  const [how, setHow] = useState<'calendar' | 'shortcut' | 'typed'>('calendar');
  // Remounts the calendar on the new date's month after a shortcut or a typed date.
  const [calKey, setCalKey] = useState(0);
  const [loaded, setLoaded] = useState(trip.date);
  if (trip.date !== loaded) {
    setLoaded(trip.date);
    setDate(trip.date);
    setTyped(toTyped(trip.date));
  }

  function pick(iso: string, via: typeof how) {
    setDate(iso);
    setTyped(toTyped(iso));
    setHow(via);
  }

  const beyondHorizon = !!date && (today().getTime() - fromISO(date).getTime()) / 86400000 > HORIZON_DAYS;
  const city = trip.origin?.city;

  async function onContinue() {
    if (!date) return;
    track('s2_date_set', { via: how, beyondHorizon });
    // After a partial scan S1's edits wait in the draft; `save` sends the whole trip with the date.
    const e = await save({ date });
    await goNext(e ?? undefined);
  }

  return (
    <Screen
      id="S2"
      title={t('S2.title')}
      dock={
        <Button onClick={onContinue} disabled={!date || isSaving}>
          {t('common.continue')}
        </Button>
      }
    >
      {trip.origin && trip.destination && (
        <div className="card card--flat s2-route">
          <RouteStrip from={trip.origin} to={trip.destination} light />
        </div>
      )}

      <Question>{t('S2.title')}</Question>
      <Sub>{city ? t('S2.sub', { city }) : t('S2.subNoCity')}</Sub>

      <TextField
        label={t('S2.label')}
        className="field--date"
        placeholder={t('S2.placeholder')}
        inputMode="numeric"
        maxLength={10}
        autoComplete="off"
        value={typed}
        liftExtra={() => calRef.current}
        onChange={(e) => {
          const v = mask(e.target.value);
          const iso = parseTyped(v);
          setTyped(v);
          setDate(iso);
          setHow('typed');
          if (iso) setCalKey((k) => k + 1);
        }}
        trailing={
          <button
            type="button"
            className="field__trail"
            aria-label={t('S2.openCalendar')}
            aria-expanded={calOpen}
            onClick={() => setCalOpen((o) => !o)}
          >
            <Icon name="cal" />
          </button>
        }
      />
      <div className="shortcuts">
        {SHORTCUTS.map((s) => (
          <button
            key={s.days}
            type="button"
            className="shortcut"
            onClick={() => {
              const d = today();
              d.setDate(d.getDate() - s.days);
              pick(toISO(d), 'shortcut');
              setCalKey((k) => k + 1);
              setCalOpen(false);
            }}
          >
            {t(s.key)}
          </button>
        ))}
      </div>
      {calOpen && <Calendar key={calKey} ref={calRef} value={date} onPick={(iso) => pick(iso, 'calendar')} />}
      {beyondHorizon && <Hint>{t('S2.beyondHorizon')}</Hint>}
    </Screen>
  );
}
